import { Heading, useTitleChange } from '@msaf/core-react'
import classNames from 'classnames'
import { useEffect, useState } from 'react'
import LocationMapView from '../../../../components/wells/location-map-view'
import { useGetWellExportReportData } from '../../../../hooks/use-api'
import { useWellId } from '../../../../hooks/use-well-id'
import './details.css'

export default function ViewWellExportReportDetails() {
  useTitleChange('Well Details')
  const id = useWellId()
  const { data: dataRequestExportReport } = useGetWellExportReportData(`wells/${id}/export-report/view`, id)

  // State for the report is stored as a JSON documemnt
  const [jsonData, setJsonData] = useState()

  // Re-render when the JSON has loaded
  useEffect(() => {
    if (dataRequestExportReport) {
      setJsonData(dataRequestExportReport)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataRequestExportReport])

  const labelStyle: React.CSSProperties = {
    fontFamily: 'Poppins',
    fontSize: '13px',
    fontWeight: 'normal',
  }

  const columnHeadingStyle: React.CSSProperties = {
    fontFamily: 'Poppins',
    fontWeight: 'normal',
    fontSize: '13px',
  }

  const columnDataStyle: React.CSSProperties = {
    fontFamily: 'Poppins',
    fontWeight: '550',
    fontSize: '13px',
  }

  const columnStyle: React.CSSProperties = {
    fontFamily: 'Poppins',
    fontWeight: 'normal',
    fontSize: '13px',
  }

  const dataStyle: React.CSSProperties = {
    fontFamily: 'Poppins',
    fontWeight: '550',
    fontSize: '13px',
  }

  const reportFieldContainer = classNames('report-field-container', {})
  const reportTextContainer = classNames('report-text-container', {})
  const reportColumnContainer = classNames('report-column-container', {})
  const reportGroupContainer = classNames(['report-group-container'], {})
  const reportTable = classNames('report-table', {})
  const report = classNames('report', {})

  /**
   * Renders a a single field
   */
  const renderReportField = (fieldName: String, fieldValue: any, type: Boolean = false) => {
    return (
      <>
        <label style={labelStyle}>{fieldName}:</label>
        {type === false ? (
          <div style={dataStyle}>{fieldValue == null ? '-' : fieldValue}</div>
        ) : (
          <div style={dataStyle}>{fieldValue === true ? 'Yes' : fieldValue === false ? 'No' : '-'}</div>
        )}
      </>
    )
  }

  /**
   * Renders the consent number field based on activity type
   */
  const renderConsentNumberField = (jsonHeaderData: any) => {
    const consentNumber = jsonHeaderData?.['Bore consent number']
    const activityType = (jsonHeaderData?.['Activity type'] ?? '').trim().toLowerCase()
    if (activityType === 'permitted') {
      return renderReportField('Bore consent number', 'N/A (Permitted)')
    } else {
      return renderReportField('Bore consent number', consentNumber)
    }
  }

  /**
   * Renders a table from an array of identically structured json object.
   * The keys are used as the headings
   */
  const renderReportTable = (columnValue: any[]) => {
    if (columnValue.length === 0) {
      return 'No data recorded'
    }

    // Get the column names
    const objects = []
    let columns = columnValue[0]
    let numColumns = 0
    for (const column in columns) {
      if (column.indexOf('<hide-heading>') !== -1) {
        objects.push(<div />)
      } else {
        objects.push(<div style={columnHeadingStyle}>{column.replace('<bold>', '')}</div>)
      }
      numColumns++
    }

    // Determine the column splits
    const columnPercentage: string = (Math.floor(100 / numColumns).toString() + '% ').repeat(numColumns)
    const colStyle: React.CSSProperties = {
      gridTemplateColumns: columnPercentage,
    }

    // For each individual cell, push up a div
    columnValue.forEach((row) => {
      for (let column in row) {
        if (column.indexOf('<bold>') !== -1) {
          objects.push(<div style={columnStyle}>{row[column]}</div>)
        } else {
          objects.push(<div style={columnDataStyle}>{row[column]}</div>)
        }
      }
    })

    // render
    return (
      <>
        <div style={colStyle} className={reportTable}>
          {objects}
        </div>
      </>
    )
  }

  /**
   * The main render function that outputs the report content
   */

  return (
    <>
      {jsonData !== undefined ? (
        <>
          <div className={report}>
            <Heading
              level={1}
            >{`${jsonData['header']?.['Council well number']}, ${jsonData['header']?.['Name']}`}</Heading>
            <div className={reportFieldContainer}>
              {renderReportField('Council well number', jsonData['header']?.['Council well number'] ?? '')}
              {renderReportField('Name', jsonData['header']?.['Name'] ?? '')}
              {renderReportField('Type', jsonData['header']?.['Type'] ?? '')}
              {renderReportField('Use(s)', jsonData['header']?.['Uses'] ?? '')}
              {renderReportField('Status', jsonData['header']?.['Status'] ?? '')}
              {renderConsentNumberField(jsonData['header'])}
            </div>

            <hr />

            <div className={reportColumnContainer}>
              <div>
                <Heading level={2}>Location</Heading>
                <div className={reportFieldContainer}>
                  {renderReportField(
                    'Easting coordinate (confirmed)',
                    jsonData['location']?.['Easting coordinate'] ?? '',
                  )}
                  {renderReportField(
                    'Northing coordinate (confirmed)',
                    jsonData['location']?.['Northing coordinate'] ?? '',
                  )}
                  {renderReportField('Address', jsonData['location']?.['Type'] ?? '')}
                  {renderReportField('Locality', jsonData['location']?.['Uses'] ?? '')}
                  {renderReportField('Water allocation zone', jsonData['location']?.['Water allocation zone'] ?? '')}
                  {renderReportField('Water management zone', jsonData['location']?.['Water management zone'] ?? '')}
                  {renderReportField('Location description ', jsonData['location']?.['Location description'] ?? '')}
                </div>
              </div>

              <LocationMapView
                projectionType={{
                  labelId: 'coordinates',
                  value: '',
                }}
                easting={{
                  labelId: 'actualEasting',
                  value: `${jsonData['location']?.['Easting coordinate'] ?? ''}`,
                }}
                northing={{
                  labelId: 'actualNorthing',
                  value: `${jsonData['location']?.['Northing coordinate'] ?? ''}`,
                }}
              />
            </div>

            <hr />
            <div className={reportGroupContainer}>
              <Heading level={2}>Drilling Information</Heading>

              <div className={reportColumnContainer}>
                <div className={reportFieldContainer}>
                  {renderReportField('Drilling date', jsonData['drillingInformation']?.['Easting coordinate'] ?? '')}
                  {renderReportField(
                    'Drilling company',
                    jsonData['drillingInformation']?.['Drilling company name'] ?? '',
                  )}
                  {renderReportField('Drilling method', jsonData['drillingInformation']?.['Drilling method'] ?? '')}
                  {renderReportField(
                    'Development method',
                    jsonData['drillingInformation']?.['Development method'] ?? '',
                  )}
                  {renderReportField(
                    'Water level when drilled (m)',
                    jsonData['drillingInformation']?.['Water level when drilled (m)'] ?? '',
                  )}
                  {renderReportField(
                    'Water level measurement date',
                    jsonData['drillingInformation']?.['Water level measured date'] ?? '',
                  )}
                </div>
                <div className={reportFieldContainer}>
                  {renderReportField('Drilled depth (m)', jsonData['drillingInformation']?.['Drilled depth (m)'] ?? '')}
                  {renderReportField('Diameter (mm)', jsonData['drillingInformation']?.['Diameter (mm)'] ?? '')}
                  {renderReportField(
                    'Decommissioning date',
                    jsonData['drillingInformation']?.['Decommissioning date'] ?? '',
                  )}
                  {renderReportField(
                    'Decommissioning method',
                    jsonData['drillingInformation']?.['Decommissioning method'] ?? '',
                  )}
                  {renderReportField(
                    'Water level above measuring point',
                    jsonData['drillingInformation']?.['Water level above measuring point'] ?? '',
                    true,
                  )}
                </div>
              </div>
            </div>
            <hr />
            <div className={reportGroupContainer}>
              <Heading level={2}>Casings</Heading>
              {renderReportTable(jsonData['casings'])}
              <Heading level={2}>Screens</Heading>
              {renderReportTable(jsonData['screens'])}
            </div>
            <br />
            <hr />

            <div className={reportGroupContainer}>
              <Heading level={2}>Wellhead construction</Heading>

              <div className={reportColumnContainer}>
                <div className={reportFieldContainer}>
                  {renderReportField(
                    'Ground level to top of casing (m)',
                    jsonData['wellheadConstruction']?.['Ground level to top of casing (m)'] ?? '',
                  )}
                  {renderReportField(
                    'Elevation reference point description',
                    jsonData['wellheadConstruction']?.['Elevation reference point description'] ?? '',
                  )}
                  {renderReportField(
                    'Ground level correction (m)',
                    jsonData['wellheadConstruction']?.['Ground level correction (m)'] ?? '',
                  )}
                  {renderReportField(
                    'GWL measuring point description',
                    jsonData['wellheadConstruction']?.['GWL measuring point description'] ?? '',
                  )}
                  {renderReportField(
                    'GWL measuring point correction (m)',
                    jsonData['wellheadConstruction']?.['GWL measuring point correction (m)'] ?? '',
                  )}
                </div>

                {renderReportTable(jsonData['elevations'])}
              </div>
            </div>
            <hr />

            <div className={reportGroupContainer}>
              <Heading level={2}>Wellhead security</Heading>
              <div className={reportTextContainer}>
                {renderReportField('Overall rating', jsonData['wellheadSecurity']?.['Overall rating'] ?? '')}
              </div>

              <div className={reportColumnContainer}>
                <div className={reportFieldContainer}>
                  {renderReportField('Concrete pad', jsonData['wellheadSecurity']?.['Concrete pad'] ?? '', true)}
                  {renderReportField('Well cap', jsonData['wellheadSecurity']?.['Well cap'] ?? '')}
                  {renderReportField(
                    'Septic tank distance (m)',
                    jsonData['wellheadSecurity']?.['Septic tank distance (m)'] ?? '',
                  )}
                </div>
                <div className={reportFieldContainer}>
                  {renderReportField('Upstand 0.3m', jsonData['wellheadSecurity']?.['Upstand 0.3m'] ?? '', true)}
                  {renderReportField('In frost pit', jsonData['wellheadSecurity']?.['In frost pit'] ?? '', true)}
                  {renderReportField('Pump type', jsonData['drillingInformation']?.['Pump type'] ?? '')}
                </div>
              </div>
              <div className={reportTextContainer}>
                {renderReportField('Sample point desc', jsonData['wellheadSecurity']?.['Sample point desc'] ?? '')}
              </div>
            </div>

            <div className={reportGroupContainer}>
              <br />
              <hr />
              <Heading level={2}>Yield drawdown tests</Heading>

              {renderReportTable(jsonData['yieldDrawdownTests'] ?? '')}
            </div>
            <br />
            <hr />
            <div className={reportGroupContainer}>
              <Heading level={2}>Aquifer tests</Heading>
              {renderReportTable(jsonData['aquiferTests'] ?? '')}
            </div>
            <br />
            <hr />
            <div className={reportGroupContainer}>
              <Heading level={2}>Notes</Heading>
              {renderReportTable(jsonData['notes'] ?? '')}
            </div>
          </div>
        </>
      ) : (
        'jsonData is undefined'
      )}
    </>
  )
}
